import type { ChartOptions, TRBL } from 'chart.js';
import type { Options as DatalabelsOptions } from 'chartjs-plugin-datalabels/types/options';
import type { BdChartDatalabelsOptions } from './datalabels-options';
import { DATALABEL_FONT_SIZE_MAX } from './datalabels-font';

/**
 * Calculates layout padding needed to prevent datalabels from being clipped
 * at chart edges based on their alignment configuration.
 */
export function calculateDatalabelsPadding(
  config: BdChartDatalabelsOptions
): Partial<TRBL> {
  const offset = config.offset ?? 4;
  const labelPadding = config.padding ?? 4;
  const borderWidth = config.borderWidth ?? 0;
  // Datalabel font size scales with the chart area (see datalabels-font.ts).
  // Reserve padding against the upper bound so labels never clip on large tiles.
  const fontSize = DATALABEL_FONT_SIZE_MAX;
  const extraMargin = 4;
  const labelOuterSize = Math.ceil(
    fontSize + labelPadding * 2 + borderWidth * 2 + offset + extraMargin
  );

  const align = config.align ?? 'center';
  return {
    top: align === 'top' ? labelOuterSize : 0,
    bottom: align === 'bottom' ? labelOuterSize : 0,
    left: align === 'left' ? labelOuterSize : 0,
    right: align === 'right' ? labelOuterSize : 0,
  };
}

/**
 * Converts domain datalabels options to Chart.js datalabels plugin options
 * and merges them with existing chart options.
 */
export function applyDatalabelsOptions(
  options: ChartOptions,
  datalabelsConfig: BdChartDatalabelsOptions
): ChartOptions {
  // Use Record<string, unknown> for plugins since datalabels is a third-party
  // plugin type not present in chart.js core PluginOptionsByType.
  const existingPlugins = (options.plugins ?? {}) as Record<string, unknown>;

  if (!datalabelsConfig.display) {
    return {
      ...options,
      plugins: {
        ...existingPlugins,
        datalabels: {
          display: false,
        },
      } as ChartOptions['plugins'],
    };
  }

  const datalabelsOptions: DatalabelsOptions = {
    display: true,
  };

  if (datalabelsConfig.anchor) {
    datalabelsOptions.anchor = datalabelsConfig.anchor;
  }

  if (datalabelsConfig.align) {
    datalabelsOptions.align = datalabelsConfig.align;
  }

  if (datalabelsConfig.offset !== undefined) {
    datalabelsOptions.offset = datalabelsConfig.offset;
  }

  if (datalabelsConfig.color) {
    datalabelsOptions.color = datalabelsConfig.color;
  }

  if (datalabelsConfig.backgroundColor) {
    datalabelsOptions.backgroundColor = datalabelsConfig.backgroundColor;
  }

  if (datalabelsConfig.borderColor) {
    datalabelsOptions.borderColor = datalabelsConfig.borderColor;
  }

  if (datalabelsConfig.borderWidth !== undefined) {
    datalabelsOptions.borderWidth = datalabelsConfig.borderWidth;
  }

  if (datalabelsConfig.borderRadius !== undefined) {
    datalabelsOptions.borderRadius = datalabelsConfig.borderRadius;
  }

  if (datalabelsConfig.padding !== undefined) {
    datalabelsOptions.padding = datalabelsConfig.padding;
  }

  if (datalabelsConfig.formatting) {
    const fmt = new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: datalabelsConfig.formatting.minimumFractionDigits,
      maximumFractionDigits: datalabelsConfig.formatting.maximumFractionDigits,
    });
    datalabelsOptions.formatter = (value: number | null): string => {
      if (value === null || value === undefined) {
        return '';
      }
      return fmt.format(value);
    };
  }

  const rawExisting = options.layout?.padding;
  const existingPadding: Partial<TRBL> =
    typeof rawExisting === 'number'
      ? { top: rawExisting, bottom: rawExisting, left: rawExisting, right: rawExisting }
      : ((rawExisting as Partial<TRBL>) ?? {});

  const datalabelsPadding = calculateDatalabelsPadding(datalabelsConfig);

  const mergedPadding: Partial<TRBL> = {
    top:    Math.max(existingPadding.top    ?? 0, datalabelsPadding.top    ?? 0),
    bottom: Math.max(existingPadding.bottom ?? 0, datalabelsPadding.bottom ?? 0),
    left:   Math.max(existingPadding.left   ?? 0, datalabelsPadding.left   ?? 0),
    right:  Math.max(existingPadding.right  ?? 0, datalabelsPadding.right  ?? 0),
  };

  // Smart merge: preserve function-valued properties (e.g. selective display,
  // custom formatter) from the original config unless the new value is also
  // a function.  This keeps charts like DRG main-department working whose
  // datalabels rely on context-dependent callbacks.
  const existingDl =
    (existingPlugins['datalabels'] as Record<string, unknown>) ?? {};
  const newDl = datalabelsOptions as unknown as Record<string, unknown>;
  const mergedDl: Record<string, unknown> = { ...existingDl };

  for (const [key, newValue] of Object.entries(newDl)) {
    const existingValue = existingDl[key];
    if (typeof existingValue === 'function' && typeof newValue !== 'function') {
      continue;
    }
    mergedDl[key] = newValue;
  }

  return {
    ...options,
    layout: { ...options.layout, padding: mergedPadding },
    plugins: {
      ...existingPlugins,
      datalabels: mergedDl,
    } as ChartOptions['plugins'],
  };
}
